import { useState } from "react"
import { useKindeAuth } from "@kinde-oss/kinde-auth-react"
import { useUserStore } from "stores/userStore"
import { fetchUser } from "api/Kinde/users"
import {
  KindeOrgNameClaim,
  KindeRolesClaim,
  KindeUserProfile,
} from "types/kinde.types"
import { USER_ROLE } from "types/userRole.types"
import { getStashedUnit, clearStashedUnit } from "utils/serverCookies"

// Define the API call function type
type ApiCallFunction = (token: string) => Promise<KindeUserProfile>

// Define the user data that gets stored (Kinde profile + application-added unit)
interface UserWithUnit extends KindeUserProfile {
  unit: string // Added by application from organization claims
}

// Define the return type of useApiCall
type UseApiCallReturn = [() => Promise<void>, boolean]

// Define the return type of useUser
interface UseUserReturn {
  getUserData: () => Promise<void>
  isLoadingUser: boolean
}

/**
 * Custom hook for making API calls with loading state management.
 *
 * @param apiCall - The API call function to be executed.
 * @returns A tuple containing the API call function and the loading state.
 */
const useApiCall = (apiCall: ApiCallFunction): UseApiCallReturn => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { getAccessToken, getClaim } = useKindeAuth()
  const setUser = useUserStore((state) => state.setUser)

  /**
   * Executes the API call.
   *
   * @async
   * @function callApi
   * @returns Promise that resolves when the API call is complete.
   */
  const callApi = async (): Promise<void> => {
    setIsLoading(true)
    try {
      const token = await getAccessToken()
      if (!token) {
        throw new Error("No authentication token available")
      }

      const res = await apiCall(token)

      // Derive the unit from the org the ACCESS TOKEN is scoped to (its
      // org_name claim), NOT the id token "organizations" claim. The latter
      // lists every org the user belongs to, so for a multi-unit user its [0]
      // can be a different org than the active token — the conductor validates
      // the "unit" header against the token's org_name and would 400 with
      // "Unit mismatch". Reading org_name here keeps header and token in sync.
      const orgNameClaim = (await getClaim("org_name")) as KindeOrgNameClaim | null

      if (!orgNameClaim?.value) {
        throw new Error("No organization information available")
      }

      let unit = orgNameClaim.value

      // Superadmin cross-server unit switch: a unit stashed in the dq_unit
      // cookie just before hopping to this server's subdomain is applied
      // (once) instead of the token's own organization.
      const stashedUnit = getStashedUnit()
      if (stashedUnit) {
        const roles = (await getClaim("roles")) as KindeRolesClaim | null
        const isSuperAdmin = roles?.value?.some(
          (role) => role.key === USER_ROLE.SUPER_ADMIN
        )
        if (isSuperAdmin) {
          unit = stashedUnit
        }
        clearStashedUnit()
      }

      const userWithUnit: UserWithUnit = { ...res, unit }

      setUser(userWithUnit)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      console.error("Error fetching User:", errorMessage)
      throw error // Re-throw to allow calling code to handle the error
    } finally {
      setIsLoading(false)
    }
  }

  return [callApi, isLoading]
}

/**
 * Custom hook to fetch and manage user data.
 *
 * This hook provides functionality to fetch user data from an API and manage loading state.
 * It uses Zustand for state management and Kinde for authentication.
 *
 * @returns An object containing the `getUserData` function and the `isLoadingUser` boolean.
 *
 * @example
 * const { getUserData, isLoadingUser } = useUser();
 *
 * useEffect(() => {
 *   getUserData();
 * }, []);
 */
export const useUser = (): UseUserReturn => {
  const [getUserData, isLoadingUser] = useApiCall(fetchUser)

  return { getUserData, isLoadingUser }
}
